import type { NotUndefined } from '@redux-saga/types'
import { AnyAction } from '@reduxjs/toolkit'
import noop from 'lodash/noop'
import { buffers, END, eventChannel } from 'redux-saga'
import { all, call, put, take, takeEvery } from 'redux-saga/effects'

import { getMimeType } from '../lib/file-types'
import type { FileType } from './types'
import { saveImagePreview } from './upload-actions'
import { CREATE_IMAGE_PREVIEWS } from './upload-constants'

function createSrc(file: File) {
  const reader = new FileReader()
  return eventChannel(emitter => {
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      emitter(reader.result)
      emitter(END)
    }
    return noop
  }, buffers.sliding<NotUndefined>(2))
}

function fileType(file: File) {
  const bReader = new FileReader()
  return eventChannel(emitter => {
    bReader.readAsArrayBuffer(file.slice(0,4))
    bReader.onloadend = () => {
      const uint = new Uint8Array(bReader.result as ArrayBuffer)
      const bytes = []
      for (let i = 0; i < uint.length; i++) {
        bytes.push(uint[i].toString(16))
      }
      const hex = bytes.join('').toUpperCase()
      emitter(getMimeType(hex))
      emitter(END)
    }
    return noop
  }, buffers.sliding<NotUndefined>(2))
}

function* generateImagePreview(file: FileType, mimeType: string): Generator<any> {
  const channel: any = yield call(createSrc, file.file)
  const src = yield take(channel)
  yield put(saveImagePreview(file.file.name, src as string, mimeType))
}

function* generatePreviews(files: FileType[]) {
  if (files) {
    yield all(files.map(file => call(generatePreview, file)))
  }
}

function* generatePreview(file: FileType): Generator<any> {
  const mimeChannel: any = yield call(fileType, file.file)
  const mimeType: any = yield take(mimeChannel)

  if (/image/.test(mimeType)) {
    yield generateImagePreview(file, mimeType)
  } else {
    yield put(saveImagePreview(file.file.name, `${window.travURI}file.png`, mimeType))
  }
}

function* previewWatcher(): Generator<any> {
  yield takeEvery(CREATE_IMAGE_PREVIEWS, function*(action: AnyAction) {
    yield call(generatePreviews, action.files)
  })
}

export {
  createSrc,
  fileType,
  generatePreview,
  generatePreviews,
  previewWatcher
}
